/**
 * SessionState
 * Owns the per-session identity and the command token used to authenticate
 * commands sent to fragments.
 */

import { generateGuid } from '@anddone/ajs-util';

export class SessionState {
  readonly #id: string = generateGuid();

  // Never exposed via a getter — callers must use applyCommandToken to stamp it into a payload.
  readonly #commandToken: string = generateGuid();

  public get id(): string {
    return this.#id;
  }

  /**
   * Add the per-session command token to a command payload.
   *
   * The raw token is never exposed by a getter. The only way to obtain it is
   * through this method, which returns a new object with `commandToken` set.
   * This ensures callers cannot supply their own token value.
   *
   * @param payload - The command data object to augment
   * @returns A new object with the same properties plus `commandToken`
   */
  public applyCommandToken<T extends object>(payload: T): T & { commandToken: string } {
    return { ...payload, commandToken: this.#commandToken };
  }

  /**
   * Validate whether a commandToken value matches the per-session token.
   *
   * @param token - Token value from the command payload to validate
   * @returns true if the token matches, false otherwise
   */
  public validateCommandToken(token: string): boolean {
    return token === this.#commandToken;
  }
}

Object.freeze(SessionState.prototype);
